import React, { useMemo, useState, VFC } from 'react';
import { Organization } from 'src/common/interfaces/organization.interface';
import { Workspace } from 'src/common/interfaces/workspace.interface';
import { JourneyMenuPropsInterface } from 'src/common/JourneyMenu';
import { MultiSelectItem, MultiSelectWithSearch } from 'src/common/multi-select/with-search';
import OrganizationLink from 'src/common/OrganizationLink';
import { CheckmarkRegularIcon } from 'src/monet/icons';
import { useCurrentOrganization } from 'src/store/organization';

interface DashboardHomeMovePanelProps
  extends Pick<
    JourneyMenuPropsInterface,
    'openRef' | 'closeRef' | 'placement' | 'offset' | 'renderOrigin' | 'onOpen' | 'onClose'
  > {
  journeyWorkspace: Workspace;
  onBack: () => void;
  onWorkspaceChanged: (workspace: Workspace) => void;
}

const workspaceMapper = (workspace: Workspace) => ({ label: workspace.name, value: `${workspace.id}` });

export const DashboardHomeMovePanel: VFC<DashboardHomeMovePanelProps> = ({
  onWorkspaceChanged,
  journeyWorkspace,
  openRef,
  closeRef,
  renderOrigin,
  placement,
  onClose,
  onBack,
  onOpen,
}) => {
  const { currentOrganization } = useCurrentOrganization();
  const allWorkspaces = useMemo(
    () => (currentOrganization as Organization).workspaces.map(workspaceMapper),
    [currentOrganization]
  );

  const [listedWorkspaces, setListedWorkspaces] = useState<MultiSelectItem[]>([...allWorkspaces]);

  const onSearchInputChanged = (searchInput: string) => {
    if (!searchInput) {
      setListedWorkspaces([...allWorkspaces]);
      return;
    }
    setListedWorkspaces(allWorkspaces.filter((workspace) => workspace.label.toLowerCase().includes(searchInput)));
  };

  const onItemSelected = (selectedItem: MultiSelectItem) => {
    onWorkspaceChanged({ id: parseInt(selectedItem.value, 10), name: selectedItem.label } as Workspace);
  };

  const selectedOption = useMemo(() => workspaceMapper(journeyWorkspace), []);

  return (
    <MultiSelectWithSearch
      name='journey-move-workspace'
      searchPlaceholder='Search for workspace'
      items={listedWorkspaces}
      placement={placement}
      selectedItems={[selectedOption]}
      className='pb-0'
      openRef={openRef}
      onOpen={onOpen}
      onClose={onClose}
      onBack={onBack}
      hasBackButton
      closeRef={closeRef}
      hideActionOnNotFound
      renderOrigin={renderOrigin}
      renderNotFound={() => <span>No results found</span>}
      onSearchInputChange={onSearchInputChanged}
      onItemSelect={onItemSelected}
      renderListItem={(item, onClick) => {
        const isItemSelected = selectedOption.value === item.value;
        return (
          <div className='flex items-center hover:opacity-80' onClick={onClick}>
            {isItemSelected && <CheckmarkRegularIcon className='shrink-0' />}
            <div className={isItemSelected ? 'ml-2' : 'ml-7'}>{item.label}</div>
          </div>
        );
      }}
      renderFooter={() => <OrganizationLink to='/workspaces'>Manage Workspaces</OrganizationLink>}
    />
  );
};
